import { createContext, useContext, useEffect } from "react";
import type { SidebarGroup } from "@altek/ui/components/layout/AppSidebar";
import { env } from "@/lib/env";

/**
 * SEKME BAŞLIĞI EKRANIN ADINI TAŞIYOR — «Notlarım · ALTEK ERP» (8 Ekim 2026, kullanılabilirlik
 * turu; WCAG 2.4.2).
 *
 * Her ekranda «ALTEK ERP» yazıyordu: `index.html`'in tek `<title>`'ı ve onu değiştiren hiçbir
 * şey yok. Beş sekmeyle çalışan kişi hangisinin izin listesi olduğunu sekme şeridinden
 * okuyamıyordu; ekran okuyucu her gezinmede aynı adı söylüyordu.
 *
 * TEK MERKEZ: KABUK. Başlığı her sayfa kendisi yazsaydı yüz küsur ekran, yüz küsur unutulma
 * fırsatı olurdu. Ad, kişinin GÖRDÜĞÜ menü satırından okunuyor (`titledPathsOf`, en uzun ön ek —
 * `/notes/123` → «Notlarım») ve menüde olmayan ama bir yerden açılan ekranlar için kısa bir
 * listeden (`OFF_MENU_TITLES`). Tam katalog BİLEREK kullanılmıyor: yetkisi olmayan bir adresin
 * gerçek adını sekmeye yazmak, «sayfa bulunamadı» ekranının saklamaya çalıştığı şeyi —
 * ekranın VAR olduğunu — başlıktan söylemek olurdu.
 *
 * Bir ekran kabuğun bulduğu adı DÜZELTEBİLİR (`useScreenTitle`): «Sayfa bulunamadı» ekranı bunu
 * yapıyor, çünkü adres bir menü satırına benzese bile ekranda o sayfa yok.
 *
 * SOHBETİN ÖNEKİ KORUNUYOR. `useUnreadDocumentTitle` başlığın önüne `(3) ` yazıyor ve yalnız
 * sayı değişince yeniden yazıyor; burada önek sökülüp başlık yeniden kurulsaydı gezinen kişinin
 * okunmamış sayısı bir sonraki mesaja kadar kaybolurdu. Bu yüzden yazılan şey: mevcut önek +
 * yeni başlık (`withKeptPrefix`).
 */

/** Ekranın adı önce: dar sekme şeridi başlığı SONDAN kırpıyor, ayırt edici kısım görünür kalsın. */
export const TITLE_SEPARATOR = " · ";

/** `useUnreadDocumentTitle`'ın öneki — `(3) `, `(99+) `. Biçim orada tanımlı, burada okunuyor. */
const UNREAD_PREFIX = /^\(\d+\+?\)\s/;

export function composeDocumentTitle(screen: string | null | undefined, product: string): string {
  // Boş ad kurulumun KENDİ adına düşüyor, ürünün adına değil (24 Ekim 2026): sekme başlığı
  // kullanıcının gördüğü her yerde kabuğun üst çubuğuyla aynı adı söylemeli. `core.brand.product_name`
  // yoksa elimizdeki en yakın doğru ad `VITE_BRAND_NAME`; «ALTEK ERP» yalnız ürünün kendini
  // tanıttığı sistem yönetimi ekranlarına ait.
  const name = product.trim() || env.brandName;
  const title = screen?.trim();
  return title ? `${title}${TITLE_SEPARATOR}${name}` : name;
}

/** Başlıkta duran okunmamış öneki yeni başlığın önüne taşır. */
export function withKeptPrefix(current: string, next: string): string {
  const prefix = UNREAD_PREFIX.exec(current)?.[0] ?? "";
  return `${prefix}${next.replace(UNREAD_PREFIX, "")}`;
}

export interface TitledPath {
  href: string;
  label: string;
}

/** Rayın satırları ve alt satırları — kişinin gerçekten açabildiği ekranlar. */
export function titledPathsOf(groups: readonly SidebarGroup[]): TitledPath[] {
  return groups.flatMap((group) =>
    group.items.flatMap((item) => [
      { href: item.href, label: item.label },
      ...(item.children ?? []).map((child) => ({ href: child.href, label: child.label })),
    ]),
  );
}

/**
 * Menüde satırı olmayan, ama kabuktan ya da bir bildirimden açılan ekranlar — sayfanın kendi
 * başlığıyla aynı sözcükler. Kapı yine rotada: yetkisi olmayana bu adreslerde «Sayfa bulunamadı»
 * çiziliyor ve o ekran adı kendi başlığıyla eziyor.
 */
export const OFF_MENU_TITLES: readonly TitledPath[] = [
  { href: "/profile", label: "Profilim" },
  { href: "/approvals", label: "Onay Merkezi" },
  { href: "/notifications", label: "Bildirimlerim" },
  { href: "/announcements", label: "Duyurular" },
  { href: "/tasks", label: "Görev" },
  { href: "/template-documents", label: "Şablondan belge" },
  { href: "/hr/employees", label: "Çalışan kartı" },
];

/**
 * Adrese en uzun ön ekle uyan ekranın adı; uyan yoksa `null` (başlık yalnız ürünün adı olur).
 * Sorgu dizesi olan satırlar (`?panel=new`) karşılaştırılmadan önce kırpılıyor.
 */
export function screenTitleFor(pathname: string, paths: readonly TitledPath[]): string | null {
  let best: TitledPath | null = null;
  let bestLength = -1;
  for (const path of paths) {
    const href = path.href.split(/[?#]/)[0] ?? "";
    if (href === "" || href === "/") continue;
    const matches = pathname === href || pathname.startsWith(`${href}/`);
    if (matches && href.length > bestLength) {
      best = path;
      bestLength = href.length;
    }
  }
  return best?.label ?? null;
}

/**
 * Kabuğun «bu ekranın adı şu» yuvası. Değer kabuğun kendi durum ayarlayıcısı; kabuğun dışında
 * (giriş, davet, parola sıfırlama) yuva yok ve başlık doğrudan yazılıyor.
 */
export const ScreenTitleContext = createContext<((title: string | null) => void) | null>(null);

/**
 * Ekranın adını kabuğun bulduğunun YERİNE koyar — yalnız kabuğun bilemediği durumlar için
 * («Sayfa bulunamadı»). Aynı anda tek bir ekran adı geçerli: sonra bağlanan kazanır, ayrılan
 * yuvayı boşaltır.
 */
export function useScreenTitle(title: string | null): void {
  const setScreenTitle = useContext(ScreenTitleContext);
  useEffect(() => {
    if (title === null) return undefined;
    if (setScreenTitle) {
      setScreenTitle(title);
      return () => setScreenTitle(null);
    }
    // Kabuğun DIŞINDAKİ ekranlar (giriş, davet, parola sıfırlama) buradan geçiyor ve oturum
    // olmadığı için `core.brand.product_name` okunamıyor — kurulum adı ortamdan gelir.
    document.title = withKeptPrefix(document.title, composeDocumentTitle(title, env.brandName));
    return undefined;
  }, [setScreenTitle, title]);
}
